/** ECRANUL «SCENARII»: toate variantele de business case ale proiectului, pe
 *  aceleași măsurători, cu ce le deosebește și cum stau una lângă alta.
 *
 *  GRAFICUL E COST CONTRA RANDAMENT, nu o listă de cifre: o variantă se alege
 *  după unde cade în plan — mai ieftină și mai rentabilă (stânga sus) bate tot.
 *  Desenul ia toată lățimea cardului: scenariile diferă adesea cu 1–2 % la cost,
 *  iar pe o pânză îngustă punctele s-ar suprapune.
 *
 *  «Folosește» e revenirea: scenariul ales devine cel curent, iar Rezultatele și
 *  Ipotezele trec pe el. Nimic nu se pierde — cel părăsit rămâne în listă.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import Termeni, { Termen } from "./Termeni";
import { data, numar, useLimba, useT } from "../../i18n";
import type { Cheie } from "../../i18n";
import { bani, baniMilioane } from "../../structure/bani";
import { ani, type Rezumat } from "./businessCase";
import { dinServer, type RezumatServer } from "./jetoane";
import type { Scenarii, Scenariu } from "./scenarii";
import { nrSuprascrieri } from "./boq";
import { areProgram, type Program } from "./program";

/** Durata din programul unui scenariu, pentru axa și coloana «Durată». */
type Durata = { luni: number; sfarsit: string } | null;

const pc = (v: number, z = 1) => `${numar(v * 100, { minimumFractionDigits: z, maximumFractionDigits: z })} %`;

/** Numele ipotezelor care pot diferi între scenarii, pentru «ce e altfel». */
const NUME: Record<string, Cheie> = {
  cedatDinScd: "bc.r.cedat.element", pretTerenBani: "ip.pret_teren_bani",
  costuriTeren: "bc.r.cTeren.element", pretApartamenteMp: "bc.ui.pret_apartamente",
  pretTeraseMp: "bc.r.pTerase.element", pretComercialMp: "bc.r.pCom.element",
  pretParcareLoc: "bc.r.pParc.element", duratiAni: "bc.r.durata.element",
};

/** Ce e altfel pe scenariul ăsta, într-un rând: abaterile lui, pe scurt.
 *  `randuri` dă numele rândurilor de cost ajustate («Structură — tronsoanele»). */
function abateri(s: Scenariu, t: ReturnType<typeof useT>, randuri: Map<string, string>): string {
  const { ajustari, ...rest } = s.ipoteze;
  const parti = Object.entries(rest).map(([k, v]) => {
    const nume = NUME[k] ? t(NUME[k]) : k;
    const val = k === "cedatDinScd" ? pc(v as number, 0) : numar(v as number);
    return `${nume} ${val}`;
  });
  for (const [k, f] of Object.entries(ajustari ?? {})) {
    parti.push(`${randuri.get(k) ?? k} × ${numar(f, { maximumFractionDigits: 2 })}`);
  }
  const n = nrSuprascrieri(s.catalog);
  if (n) parti.push(t("sc.preturi_proprii", { n }));
  if (areProgram(s.program)) {
    for (const [k, v] of Object.entries(s.program ?? {})) {
      parti.push(k === "muncitori" || k === "lag_zile"
        ? Object.entries(v as Record<string, number>).map(([c, x]) => `${c} ${x}`).join(", ")
        : `${k} ${Array.isArray(v) ? v.join(",") : String(v)}`);
    }
  }
  return parti.join(" · ") || t("sc.fara_abateri");
}

type Punct = { s: Scenariu; r: Rezumat };

/** Planul cost (TIC) contra randament (ROI pe an) sau contra durată (luni,
 *  din program): o variantă se alege după unde cade în plan. */
function Grafic({ puncte, curent, ales, laAlege, axa, durata }: {
  puncte: Punct[]; curent: string; ales: Set<string>; laAlege: (id: string) => void;
  axa: "roi" | "durata"; durata: (id: string) => Durata | undefined;
}) {
  const t = useT();
  const [peste, setPeste] = useState<string | null>(null);
  if (axa === "durata") puncte = puncte.filter((p) => durata(p.s.id)?.luni != null);
  const yv = (p: Punct) => axa === "roi" ? p.r.roiPeAn : (durata(p.s.id)?.luni ?? 0);
  const yf = (v: number) => axa === "roi" ? pc(v) : numar(v, { maximumFractionDigits: 1 });
  // pânza în pixelii cardului, nu un viewBox fix întins: textul axelor rămâne
  // de 11 px, iar lățimea în plus merge toată în rezoluția pe cost
  const gazda = useRef<HTMLDivElement>(null);
  const [L, setL] = useState(960);
  useEffect(() => {
    const el = gazda.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver(() => setL(Math.max(480, Math.round(el.clientWidth - 24))));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const H = 420, m = { s: 64, d: 24, sus: 18, jos: 44 };
  const xs = puncte.map((p) => p.r.tic), ys = puncte.map(yv);
  if (!puncte.length) return <div className="sc-graf"><span className="bc-um">{t("sc.durata_se_calculeaza")}</span></div>;
  const rx = [Math.min(...xs), Math.max(...xs)], ry = [Math.min(...ys, 0), Math.max(...ys, 0)];
  // o marjă de 8 % pe fiecare latură, și o întindere minimă, ca un singur
  // scenariu (sau mai multe cu același cost) să nu cadă pe margine
  const ext = (a: number[], min: number) => {
    const w = Math.max(a[1] - a[0], min);
    const c = (a[0] + a[1]) / 2;
    return [c - w * 0.58, c + w * 0.58];
  };
  const [x0, x1] = ext(rx, Math.max(rx[1] * 0.04, 1));
  const [y0, y1] = ext(ry, 0.01);
  const X = (v: number) => m.s + (v - x0) / (x1 - x0) * (L - m.s - m.d);
  const Y = (v: number) => H - m.jos - (v - y0) / (y1 - y0) * (H - m.sus - m.jos);
  const pas = (a: number, b: number, n: number) => Array.from({ length: n + 1 }, (_, k) => a + (b - a) * k / n);
  const dupaId = new Map(puncte.map((p) => [p.s.id, p]));
  const p0 = peste ? dupaId.get(peste) : null;
  return (
    <div className="sc-graf" ref={gazda}>
      <svg viewBox={`0 0 ${L} ${H}`} role="img" aria-label={t("sc.grafic_aria")}>
        {pas(y0, y1, 4).map((v) => (
          <g key={`y${v}`}>
            <line x1={m.s} x2={L - m.d} y1={Y(v)} y2={Y(v)} className="sc-grila" />
            <text x={m.s - 8} y={Y(v) + 4} textAnchor="end" className="sc-ax">{yf(v)}</text>
          </g>
        ))}
        {y0 < 0 && y1 > 0 && <line x1={m.s} x2={L - m.d} y1={Y(0)} y2={Y(0)} className="sc-zero" />}
        {pas(x0, x1, 4).map((v) => (
          <text key={`x${v}`} x={X(v)} y={H - m.jos + 18} textAnchor="middle" className="sc-ax">
            {baniMilioane(v)}</text>
        ))}
        <text x={(m.s + L - m.d) / 2} y={H - 6} textAnchor="middle" className="sc-ax">{t("sc.ax_cost")}</text>
        <text x={14} y={(H - m.jos) / 2} textAnchor="middle" className="sc-ax"
              transform={`rotate(-90 14 ${(H - m.jos) / 2})`}>{t(axa === "roi" ? "sc.ax_roi" : "sc.ax_durata")}</text>
        {puncte.map((p) => {
          const x = X(p.r.tic), y = Y(yv(p));
          const e = p.s.id === curent;
          return (
            <g key={p.s.id} className="sc-pct" onMouseEnter={() => setPeste(p.s.id)}
               onMouseLeave={() => setPeste(null)} onClick={() => laAlege(p.s.id)}>
              <circle cx={x} cy={y} r={14} fill="transparent" />
              <circle cx={x} cy={y} r={4} className="sc-dot" />
              {(e || ales.has(p.s.id) || puncte.length <= 6) && (
                <text x={x + 8} y={y - 7} className={`sc-et${e ? " sc-et-curent" : ""}`}>{p.s.nume}</text>
              )}
            </g>
          );
        })}
      </svg>
      {p0 && (
        <div className="sc-tip" role="status">
          <b>{p0.s.nume}</b>
          <span>{t("bc.ui.tic")}: {baniMilioane(p0.r.tic)}</span>
          <span>{t("bc.ui.profit_net")}: {baniMilioane(p0.r.profitNet)}</span>
          <span><Termeni text={t("sc.roi_an")} />: {pc(p0.r.roiPeAn)}</span>
          <span>{t("bc.r.durata.element")}: {durata(p0.s.id)?.luni != null
            ? t("sc.luni", { n: numar(durata(p0.s.id)!.luni, { maximumFractionDigits: 1 }) })
            : `${ani(p0.r.ipoteze.duratiAni)} ${t("bc.um.ani")}`}</span>
        </div>
      )}
    </div>
  );
}

export default function PanouScenarii({ rezCurent, rezumatPentru, programPentru, sc, potScrie, laRezultate }: {
  /** rezumatul scenariului curent, cel de pe ecran */
  rezCurent: Rezumat;
  /** rezumatul unui alt scenariu, de la server (fiecare pe lista și programul
   *  lui); `null` când nu s-a putut calcula */
  rezumatPentru: (s: Scenariu) => Promise<RezumatServer | null>;
  /** programul de execuție al unui scenariu (durata lui); lipsă = fără durată */
  programPentru?: (s: Scenariu) => Promise<Program | null>;
  sc: Scenarii;
  potScrie: boolean;
  laRezultate: () => void;
}) {
  const t = useT();
  const limba = useLimba();
  const lista = sc.stare?.scenarii.length ? sc.stare.scenarii : [sc.curent];
  const curent = sc.curent.id;
  const [ales, setAles] = useState<Set<string>>(new Set());
  const [redenumit, setRedenumit] = useState<{ id: string; nume: string } | null>(null);
  const [axa, setAxa] = useState<"roi" | "durata">("roi");
  // durata fiecărui scenariu, din programul lui: cerută o dată, când apare
  const [durate, setDurate] = useState<Record<string, Durata>>({});
  const cheiaProgramului = (s: Scenariu) => `${s.id}|${JSON.stringify(s.program ?? null)}|${nrSuprascrieri(s.catalog)}`;
  useEffect(() => {
    if (!programPentru) return;
    for (const s of lista) {
      const k = cheiaProgramului(s);
      if (k in durate) continue;
      setDurate((d) => ({ ...d, [k]: null }));
      void programPentru(s).then((p) => setDurate((d) => ({ ...d, [k]: p ? { luni: p.luni, sfarsit: p.sfarsit } : null })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lista, programPentru]);
  const durata = (id: string): Durata | undefined => {
    const s = lista.find((x) => x.id === id);
    return s ? durate[cheiaProgramului(s)] : undefined;
  };

  // rezumatele celorlalte scenarii, de la server: cerute o dată pe scenariu
  // (pe abaterile, catalogul și programul lui), scrise aici în limba ecranului
  const [rezumate, setRezumate] = useState<Record<string, RezumatServer | null | undefined>>({});
  const cheiaRezumatului = (s: Scenariu) =>
    `${s.id}|${JSON.stringify(s.ipoteze)}|${JSON.stringify(s.catalog ?? null)}|${JSON.stringify(s.program ?? null)}`;
  useEffect(() => {
    for (const s of lista) {
      if (s.id === curent) continue;
      const k = cheiaRezumatului(s);
      if (k in rezumate) continue;
      setRezumate((d) => ({ ...d, [k]: undefined }));
      void rezumatPentru(s).then((r) => setRezumate((d) => ({ ...d, [k]: r })), () => setRezumate((d) => ({ ...d, [k]: null })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lista, curent, rezumatPentru]);
  const puncte: Punct[] = useMemo(() => lista.flatMap((s) => {
    if (s.id === curent) return [{ s, r: rezCurent }];
    const r = rezumate[cheiaRezumatului(s)];
    return r ? [{ s, r: dinServer(r) }] : [];
    // limba: textele rezumatului se scriu la desen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [lista, curent, rezCurent, rezumate, limba]);
  const comparate = puncte.filter((p) => ales.has(p.s.id) || p.s.id === curent);
  const numeRanduri = useMemo(() => new Map((puncte[0]?.r.sectiuni ?? [])
    .flatMap((x) => x.randuri).map((r) => [r.cheie, r.element])), [puncte]);

  const comuta = (id: string) => {
    const n = new Set(ales);
    if (n.has(id)) n.delete(id); else n.add(id);
    setAles(n);
  };
  const nou = () => {
    void sc.creeaza({}, { nume: t("sc.nume_nou", { n: lista.length + 1 }), autor: "om" }).catch(() => {});
  };

  const randuri: { cheie: Cheie; v: (r: Rezumat) => string }[] = [
    { cheie: "bc.ui.tic", v: (r) => bani(r.tic, { zecimale: 0 }) },
    { cheie: "bc.ui.venituri", v: (r) => bani(r.venituri, { zecimale: 0 }) },
    { cheie: "bc.ui.profit_net", v: (r) => bani(r.profitNet, { zecimale: 0 }) },
    { cheie: "sc.roi", v: (r) => pc(r.roi) },
    { cheie: "sc.roi_an", v: (r) => pc(r.roiPeAn) },
    { cheie: "bc.ui.echilibru", v: (r) => `${bani(r.pragRentabilitate)}/m²` },
  ];
  const luni = (id: string) => {
    const d = durata(id);
    return d?.luni != null ? t("sc.luni", { n: numar(d.luni, { maximumFractionDigits: 1 }) }) : "—";
  };

  return (
    <div className="sc">
      <div className="ip-sus">
        <span className="bc-um" style={{ flex: "1 1 320px" }}>{t("sc.intro")}</span>
        {sc.seSalveaza && <span className="bc-um">{t("comun.se_salveaza")}</span>}
        {sc.eroare && <span className="ip-err">{sc.eroare}</span>}
        {potScrie && <button type="button" className="btn" onClick={nou}>{t("sc.nou")}</button>}
      </div>

      <section className="ip-card" aria-labelledby="sc-graf-t">
        <div className="ip-card-h"><h2 id="sc-graf-t">{t("sc.grafic")}</h2>
          <span className="bc-um">{t("sc.grafic_sub")}</span>
          {programPentru && (
            <>
              <span style={{ flex: "1 1 auto" }} />
              <div className="tabs" role="tablist">
                <button type="button" role="tab" aria-selected={axa === "roi"} className={axa === "roi" ? "on" : ""}
                        onClick={() => setAxa("roi")}><Termeni text={t("sc.axa_roi")} /></button>
                <button type="button" role="tab" aria-selected={axa === "durata"} className={axa === "durata" ? "on" : ""}
                        onClick={() => setAxa("durata")}><Termeni text={t("sc.axa_durata")} /></button>
              </div>
            </>
          )}
        </div>
        <Grafic puncte={puncte} curent={curent} ales={ales} laAlege={comuta} axa={axa} durata={durata} />
      </section>

      <section className="ip-card" aria-labelledby="sc-lista-t">
        <div className="ip-card-h"><h2 id="sc-lista-t">{t("sc.lista")}</h2>
          <span className="bc-um">{t("sc.lista_sub")}</span></div>
        <div className="bc-scroll">
          <table className="sc-tab">
            <thead><tr>
              <th aria-label={t("sc.compara")} />
              <th>{t("sc.th.scenariu")}</th>
              <th>{t("sc.th.ce_e_altfel")}</th>
              <th className="bc-n"><Termen termen="tic">{t("bc.ui.tic")}</Termen></th>
              <th className="bc-n">{t("bc.ui.profit_net")}</th>
              <th className="bc-n"><Termeni text={t("sc.roi_an")} /></th>
              {programPentru && <th className="bc-n">{t("sc.th.durata")}</th>}
              <th />
            </tr></thead>
            <tbody>
              {[...puncte].reverse().map(({ s, r }) => (
                <tr key={s.id} className={s.id === curent ? "sc-curent" : ""}>
                  <td><input type="checkbox" checked={ales.has(s.id) || s.id === curent}
                             disabled={s.id === curent} onChange={() => comuta(s.id)}
                             aria-label={t("sc.compara_cu", { nume: s.nume })} /></td>
                  <td>
                    {redenumit?.id === s.id ? (
                      <input className="sc-nume" autoFocus value={redenumit.nume}
                             aria-label={t("sc.redenumeste")}
                             onChange={(e) => setRedenumit({ id: s.id, nume: e.target.value })}
                             onBlur={() => { void sc.redenumeste(s.id, redenumit.nume).catch(() => {});
                                             setRedenumit(null); }}
                             onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                                                 if (e.key === "Escape") setRedenumit(null); }} />
                    ) : (
                      <span className="sc-nm">
                        <b>{s.nume}</b>
                        {s.id === curent && <span className="ip-pill ip-pill-acc">{t("sc.curent")}</span>}
                        <span className={`ip-pill ${s.autor === "om" ? "ip-pill-mut" : "ip-pill-met"}`}>
                          {t(`sc.autor.${s.autor}`)}</span>
                      </span>
                    )}
                    <div className="bc-um">{s.creat_la ? new Date(s.creat_la).toLocaleString() : ""}
                      {s.nota ? ` · ${s.nota}` : ""}</div>
                  </td>
                  <td className="bc-obs">{abateri(s, t, numeRanduri)}</td>
                  <td className="bc-n bc-cif">{baniMilioane(r.tic)}</td>
                  <td className={`bc-n bc-cif${r.profitNet < 0 ? " bc-banda-neg-t" : ""}`}>
                    {baniMilioane(r.profitNet)}</td>
                  <td className="bc-n bc-cif">{pc(r.roiPeAn)}</td>
                  {programPentru && <td className="bc-n bc-cif">{luni(s.id)}</td>}
                  <td className="sc-act">
                    {s.id !== curent && potScrie && (
                      <button type="button" className="btn"
                              onClick={() => { void sc.alege(s.id).then(laRezultate).catch(() => {}); }}>
                        {t("sc.foloseste")}</button>
                    )}
                    {potScrie && (
                      <button type="button" className="btn btng"
                              onClick={() => setRedenumit({ id: s.id, nume: s.nume })}>
                        {t("sc.redenumeste")}</button>
                    )}
                    {s.id !== curent && potScrie && (
                      <button type="button" className="btn btng"
                              onClick={() => { void sc.sterge(s.id).catch(() => {}); }}>
                        {t("sc.sterge")}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {comparate.length > 1 && (
        <section className="ip-card" aria-labelledby="sc-cmp-t">
          <div className="ip-card-h"><h2 id="sc-cmp-t">{t("sc.comparatie")}</h2></div>
          <div className="bc-scroll">
            <table className="sc-tab">
              <thead><tr>
                <th />
                {comparate.map((p) => <th key={p.s.id} className="bc-n">{p.s.nume}</th>)}
              </tr></thead>
              <tbody>
                {randuri.map((x) => (
                  <tr key={x.cheie}>
                    <td>{t(x.cheie)}</td>
                    {comparate.map((p) => (
                      <Fragment key={p.s.id}>
                        <td className={`bc-n bc-cif${p.s.id === curent ? " sc-col-curent" : ""}`}>
                          {x.v(p.r)}</td>
                      </Fragment>
                    ))}
                  </tr>
                ))}
                {programPentru && (
                  <>
                    <tr>
                      <td>{t("sc.th.durata")}</td>
                      {comparate.map((p) => <td key={p.s.id} className={`bc-n bc-cif${p.s.id === curent ? " sc-col-curent" : ""}`}>{luni(p.s.id)}</td>)}
                    </tr>
                    <tr>
                      <td>{t("sc.th.sfarsit")}</td>
                      {comparate.map((p) => <td key={p.s.id} className="bc-n bc-cif">{durata(p.s.id)?.sfarsit ? data(durata(p.s.id)!.sfarsit) : "—"}</td>)}
                    </tr>
                  </>
                )}
                <tr>
                  <td>{t("sc.th.ce_e_altfel")}</td>
                  {comparate.map((p) => <td key={p.s.id} className="bc-obs bc-n">{abateri(p.s, t, numeRanduri)}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
